import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { createScheduleEvent } from '../../services/scheduleApi';
import { SCHEDULE_TYPE_STYLES } from '../../services/scheduleEvents';

const DURATION_OPTIONS = ['30', '45', '60', '90', '120'];

// Forma rápida de agendar desde el perfil del cliente — escribe en la misma
// colección que la Agenda general (companyScheduleEvents), así que aparece
// ahí también en vez de quedar aislado en un rincón que nadie más ve.
const ClientAgenda = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [form, setForm] = useState({
    title: '',
    type: 'follow_up',
    date: '',
    time: '',
    duration: '60',
    notes: '',
  });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;
      setLoadingClient(true);
      try {
        const ref = doc(db, 'clients', clientId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setClient({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error('❌ Error cargando cliente para agenda:', err);
      } finally {
        setLoadingClient(false);
      }
    };

    fetchClient();
  }, [clientId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (status) setStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client) {
      setStatus({ type: 'error', message: 'Cliente no encontrado.' });
      return;
    }
    const trimmedTitle = form.title.trim();
    if (!trimmedTitle) {
      setStatus({ type: 'error', message: 'Añade un título para el evento.' });
      return;
    }
    if (!form.date || !form.time) {
      setStatus({ type: 'error', message: 'Selecciona fecha y hora.' });
      return;
    }

    const startAt = new Date(`${form.date}T${form.time}`);
    if (Number.isNaN(startAt.getTime())) {
      setStatus({ type: 'error', message: 'Fecha u hora inválida.' });
      return;
    }

    const durationMinutes = Math.max(parseInt(form.duration, 10) || 60, 15);
    const endAt = new Date(startAt.getTime() + durationMinutes * 60000);
    const notes = form.notes.trim();

    setSubmitting(true);
    setStatus(null);
    try {
      const result = await createScheduleEvent({
        title: trimmedTitle,
        type: form.type,
        status: 'scheduled',
        startAt,
        endAt,
        clientId: client.id,
        contactName: client.businessName || client.name || client.email,
        contactEmail: client.email || null,
        notes,
      });

      if (result?.syncStatus === 'failed') {
        setStatus({ type: 'warning', message: result.warning || 'Evento guardado en BBTS; la sincronización con Google Calendar requiere revisión.' });
      } else {
        setStatus({ type: 'success', message: client.email ? 'Evento programado y enviado al calendario.' : 'Evento programado en la agenda.' });
      }
      setForm({ title: '', type: 'follow_up', date: '', time: '', duration: '60', notes: '' });
    } catch (err) {
      console.error('❌ Error creando agenda del cliente:', err);
      setStatus({ type: 'error', message: 'No pudimos crear este evento. Intenta de nuevo.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingClient) {
    return <p className="text-sm text-gray-500">Cargando cliente...</p>;
  }

  if (!client) {
    return <p className="text-sm text-red-500">No se encontró este cliente.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Programar visita / llamada</h2>
          <p className="text-sm text-gray-500">
            Cliente: <span className="font-medium text-gray-700">{client.businessName || client.name || client.email}</span>
          </p>
          {!client.email && (
            <p className="text-xs text-amber-600 mt-1">
              Este cliente no tiene correo — el evento se guarda en la agenda pero no se manda confirmación ni se sincroniza con Google Calendar.
            </p>
          )}
        </div>
        <Link to="/admin/calendar" className="text-xs font-semibold text-orange-600 hover:underline">
          Ver agenda completa →
        </Link>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="block text-sm font-medium text-gray-700">Motivo</label>
          <input
            type="text"
            name="title"
            value={form.title}
            onChange={handleInputChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
            placeholder="Llamada, visita, demostración..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo</label>
          <select
            name="type"
            value={form.type}
            onChange={handleInputChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
          >
            {Object.entries(SCHEDULE_TYPE_STYLES).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Fecha</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hora</label>
            <input
              type="time"
              name="time"
              value={form.time}
              onChange={handleInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Duración</label>
            <select
              name="duration"
              value={form.duration}
              onChange={handleInputChange}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
            >
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} min
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notas internas</label>
          <textarea
            name="notes"
            rows="3"
            value={form.notes}
            onChange={handleInputChange}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
            placeholder="Detalles que quieras recordar"
          ></textarea>
        </div>

        {status && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ${
              status.type === 'success'
                ? 'bg-green-50 text-green-700'
                : status.type === 'warning'
                  ? 'bg-amber-50 text-amber-800'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500">
            {client.email
              ? `Se creará el evento en Google Calendar y se enviará confirmación al cliente (${client.email}).`
              : 'Se guardará en la agenda interna.'}
          </p>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 disabled:opacity-60"
          >
            {submitting ? 'Guardando...' : 'Agendar evento'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClientAgenda;
